import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { SelectButtonModule } from 'primeng/selectbutton';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { SkeletonModule } from 'primeng/skeleton';
import { MessageModule } from 'primeng/message';
import { FormsModule } from '@angular/forms';
import { Measurement, Stage } from '@cosmos/shared';
import { UiPrefsService } from '@cosmos/angular/utils';
import { FolderCardComponent } from './folder-card.component';
import { MeasurementRowComponent } from './measurement-row.component';
import { MeasurementCardComponent } from '../../cards/measurement-card/measurement-card.component';

@Component({
  selector: 'cosmos-stage-browser',
  imports: [
    TranslatePipe, FormsModule, SelectButtonModule, ButtonModule, CardModule, SkeletonModule, MessageModule,
    FolderCardComponent, MeasurementRowComponent, MeasurementCardComponent,
  ],
  templateUrl: './stage-browser.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: `
    /* The card owns the outer border; rows are divided by the container's
       divide-y (so there is no last-child border to strip). */
    .stage-browser-list ::ng-deep .p-card-body { padding: 0.25rem; }
    .stage-browser-list ::ng-deep .p-card-content { padding: 0; }
    /* The stage/measurement boundary: a thicker rule than the divide-y. */
    .stage-browser-list__boundary {
      height: 1px;
      background: var(--p-primary-200);
    }
    :host-context(.dark) .stage-browser-list__boundary,
    .dark .stage-browser-list__boundary {
      background: var(--p-primary-300);
    }
  `,
})
export class StageBrowserComponent {
  private readonly uiPrefs = inject(UiPrefsService);

  readonly stages = input<Stage[]>([]);
  readonly measurements = input<Measurement[]>([]);
  readonly stagesLoading = input<boolean>(false);
  readonly stagesError = input<boolean>(false);
  readonly measurementsLoading = input<boolean>(false);
  readonly measurementsError = input<boolean>(false);
  readonly canEdit = input<boolean>(false);
  readonly showAddMeasurement = input<boolean>(false);
  readonly showSuperadminTools = input<boolean>(false);
  readonly compareLoading = input<boolean>(false);
  readonly summarizeLoading = input<boolean>(false);

  readonly openStage = output<string>();
  readonly openMeasurement = output<string>();
  readonly createStage = output<void>();
  readonly createMeasurement = output<void>();
  readonly compare = output<void>();
  readonly summarize = output<void>();
  readonly renameStage = output<string>();
  readonly moveStage = output<string>();
  // Stage deletion is delegated to the f-admin app — not exposed here.

  protected readonly viewMode = this.uiPrefs.stageViewMode;
  protected setViewMode(mode: 'grid' | 'list'): void { this.uiPrefs.setStageViewMode(mode); }

  protected readonly viewModeOptions = [
    { icon: 'fa-solid fa-grip', value: 'grid' as const },
    { icon: 'fa-solid fa-list', value: 'list' as const },
  ];

  // Selection scaffolding — built now so STAGE_OPEN_GESTURE can flip to
  // 'double' with no further plumbing. v1 only surfaces an informative
  // selection summary; no bulk-action toolbar yet.
  private readonly selectedIds = signal<Set<string>>(new Set());

  protected readonly selectedStageCount = computed(
    () => this.stages().filter((s) => !!s._id && this.selectedIds().has(s._id)).length,
  );
  protected readonly selectedMeasurementCount = computed(
    () => this.measurements().filter((m) => !!m._id && this.selectedIds().has(m._id)).length,
  );
  protected readonly hasSelection = computed(
    () => this.selectedStageCount() > 0 || this.selectedMeasurementCount() > 0,
  );

  isSelected(id: string | undefined): boolean {
    return !!id && this.selectedIds().has(id);
  }

  toggleSelected(id: string | undefined): void {
    if (!id) return;
    this.selectedIds.update((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  clearSelection(): void {
    this.selectedIds.set(new Set());
  }

  /** Guarded re-emitters — Stage/Measurement._id is optional in the shared
   *  models, but a rendered row always has one. These keep the public
   *  outputs typed as `string` without `!` assertions in the template. */
  emitOpenStage(id: string | undefined): void { if (id) this.openStage.emit(id); }
  emitOpenMeasurement(id: string | undefined): void { if (id) this.openMeasurement.emit(id); }
  emitRenameStage(id: string | undefined): void { if (id) this.renameStage.emit(id); }
  emitMoveStage(id: string | undefined): void { if (id) this.moveStage.emit(id); }
}
